import { Box, Button, Checkbox, IconButton, InputBase, ListItemText, makeStyles, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@material-ui/core";
import { blue } from "@material-ui/core/colors";
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import clsx from "clsx";
import { forwardRef, memo, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { atom, atomFamily, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useAlert } from "../../hooks/useAlert";
import useEffectSkipFirst from "../../hooks/useEffectSkipFirst";
import { Loading } from "../../parts/Loading";
import { OnClickAnchor } from "../../parts/OnClickAnchor";
import { aircraftGroupExternalListFilterWordState, aircraftGroupListFilterWordState, useAircraftGroupLinkageUserGroup, userGroupSelectedIdState } from "../../store/AircraftSettingState";
import { SUBJ_AIRCRAFT_GROUP_LINKAGE_USER_GROUP, VERB_UPDATE } from "../../utils/AccessControl";
import RoleControl from "../RoleControl";
import { AircraftSettingMenu } from "./AircraftSetting";

const useStyles = makeStyles({
	container: {
		height: '100%',
		// border: '1px solid #a7a7a7',
	},
	stickyTable: {
		borderCollapse: 'separate',
		marginBottom: '1px',
	},
	fixedCell: {
		position: 'sticky',
		left: 0,
	},
	tableCell: {
		backgroundColor: 'white',
		borderRight: '1px solid #a7a7a7',
		borderBottom: '1px solid #a7a7a7',
		'th&': {
			borderTop: '1px solid #a7a7a7',
		},
		'&:nth-child(1)': {
			position: 'sticky',
			left: 0,
			zIndex: 2,
			borderLeft: '1px solid #a7a7a7',
		},
		// '&:nth-child(2)': {
		// 	position: 'sticky',
		// 	left: '34px',
		// 	zIndex: 2,
		// 	whiteSpace: 'nowrap',
		// },
		verticalAlign: 'baseline',
	},
	headerCell: {
		color: '#fff',
		backgroundColor: '#4c4f53',
		whiteSpace: 'nowrap',
		position: 'sticky',
		zIndex: 3,
		top: 0,
		verticalAlign: 'middle',
		'&:nth-child(-n+1)': {
			zIndex: 4
		},
		'tr:nth-child(2) &': {
			zIndex: 2,
			top: 38,
			borderTop: 'none',
			borderLeft: 'none',
		},
		'& a:link, a:visited': {
			color: '#fff',
		},
	},
	imageCell: {
		padding: '4px',
		width: '72px',
		boxSizing: 'border-box',
		'& img': {
			display: 'block',
			margin: '0 auto',
		}
	},
	alignRight: {
		textAlign: 'right',
	},
	svgCell: {
		verticalAlign: 'bottom',
	},
	filterBox: {
		padding: '2px 4px',
		display: 'flex',
		alignItems: 'center',
		width: 400,
		marginBottom: 1,
		marginRight: 'auto',
	},
	input: {
		marginLeft: 8,
		flex: 1,
		"& .MuiInputBase-input": {
			padding: '3px 0 4px',
		}
	},
	iconButton: {
		padding: 1,
	},
	closeButton: {
		padding: 2,
	},
	searchIcon: {
		color: 'rgba(0, 0, 0, 0.54)',
	},
	formInputText: {
		'& .MuiFormLabel-root': {
			color: 'black',
			'&.Mui-error': {
				color: '#f44336',
			}
		},
		'&:not(:first-child)': {
			marginTop: '20px',
		},
		'& .required': {
			color: '#f44336',
		}
	},
	commission: {
		backgroundColor: '#d9ead3ff',
	},
	agreement: {
		backgroundColor: '#f4ccccff',
	},
	sharedCell: {
		height: 0,
	},
	headerGroupCell: {
		paddingRight: 10,
	},
	checkCell: {
		verticalAlign: 'middle',
		transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
	},
	checkIcon: {
		width: 24,
		height: 24,
		margin: '0 auto',
		'& .MuiSvgIcon-root': {
			color: 'rgb(2, 136, 209)',
		}
	},
	modifiedCell: {
		backgroundColor: 'orange',
	},
	smallCheckbox: {
		padding: 2,
	},
	hidden: {
		visibility: 'hidden',
	},
	filterIconBox: {
		position: 'absolute',
		right: 0,
		marginTop: '-43px',
		marginRight: '10px',
		zIndex: 10,
	},
	filterIcon: {
		padding: 6,
		color: '#a7a7a7',
	},
	filterIconFiltered: {
		color: blue[400],
	},
});

const checkAllUserGroupState = atomFamily({
	key: 'checkAllUserGroupState',
	default: false,
});

const resetUserGroupState = atom({
	key: 'resetUserGroupState',
	default: 0,
});

const columns = [
	{ id: 'aircraftgroup_name', label: 'Aircraft Group' },
	{ id: 'usergroup_name', label: 'User Group' },
];

const SelectCheckboxList = (props) => {
	const { title, dataList, enableList, setEnableList, icon } = props;
	const [open, setOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);

	const handleClickIconButton = (event) => {
		setAnchorEl(event.currentTarget);
		setOpen(prev => !prev);
	}

	const handleClickAway = () => {
		setOpen(false);
	}

	const handleCheckboxChange = (agid) => {
		setEnableList(prev => {
			return {
				...prev,
				[agid]: !prev[agid]
			};
		});
	}

	const classes = useStyles();

	return (
		<>
			<Tooltip title={title}>
				<IconButton onClick={handleClickIconButton} className={clsx(classes.filterIcon, Object.values(enableList).length !== Object.values(enableList).filter(v => v).length && classes.filterIconFiltered)}>
					{icon}
				</IconButton>
			</Tooltip>
			<Menu
				open={open}
				anchorEl={anchorEl}
				onClose={handleClickAway}
			>
				{dataList.map((row, index) => {
					return (
						<MenuItem dense key={`SelectCheckboxList-${row.text}-${index}`} onClick={() => handleCheckboxChange(row.usergroup_id)}>
							<Checkbox checked={enableList[row.usergroup_id]} size='small'></Checkbox>
							<ListItemText>{row.usergroup_name}</ListItemText>
						</MenuItem>
					)
				})}
			</Menu>
		</>
	)
};

const UserGroupListTableHeaderCell = memo(forwardRef((props, ref) => {
	const { userGroupId, userGroupName, editMode, editable } = props;
	const reset = useRecoilValue(resetUserGroupState);
	const [checkAll, setCheckAll] = useRecoilState(checkAllUserGroupState(userGroupId));

	useEffect(() => {
		setCheckAll(false);
	}, [reset, setCheckAll]);

	const handleCheck = useCallback((value) => {
		setCheckAll(value);
	}, [setCheckAll]);

	const classes = useStyles();

	return (
		<TableCell
			style={{ minWidth: 0 }}
			className={clsx(classes.headerCell, classes.tableCell, classes.headerGroupCell)}
		>
			{userGroupName}
			<Checkbox
				checked={checkAll}
				onChange={(event) => handleCheck(event.target.checked)}
				className={clsx(classes.smallCheckbox, !(editable && editMode) && classes.hidden)}
			/>
		</TableCell>
	);
}));

const UserGroupListTableCell = memo(forwardRef((props, ref) => {
	const { aircraftGroupId, userGroupId, initCheck, editMode, editable } = props;
	const reset = useRecoilValue(resetUserGroupState);
	const checkAll = useRecoilValue(checkAllUserGroupState(userGroupId));
	const [check, setCheck] = useState(() => {
		if (ref.current?.[aircraftGroupId]?.[userGroupId] !== undefined) {
			return ref.current[aircraftGroupId][userGroupId];
		}
		return initCheck;
	});

	useEffectSkipFirst(() => {
		setCheck(initCheck);
	}, reset);

	useEffectSkipFirst(() => {
		if (editable && editMode) {
			handleCheck(checkAll);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, checkAll);

	const handleCheck = useCallback((value) => {
		setCheck(value);
		ref.current[aircraftGroupId] = Object.assign(ref.current[aircraftGroupId] || {}, {[userGroupId]: value});
	}, [aircraftGroupId, userGroupId, ref]);

	const classes = useStyles();

	return (
		<TableCell
			align="center"
			padding="checkbox"
			style={{ minWidth: 0 }}
			className={clsx(classes.tableCell, classes.checkCell, editMode && initCheck !== check && classes.modifiedCell)}
		>
			{!(editable && editMode) ?
				check ?
					<Box className={clsx(classes.checkIcon)}>
						<CheckIcon color="secondary" />
					</Box>
					:
					<></>
				:
				<Checkbox checked={check} onChange={(event) => handleCheck(event.target.checked)} />
			}
		</TableCell>
	)
}));

const UserGroupListTableRow = memo(forwardRef((props, ref) => {
	const { userGroupList, aircraftGroupId, initCheckList, editMode } = props;

	// const classes = useStyles();
	// const {t} = useTranslation();

	return (
		userGroupList.map(group => (
			<UserGroupListTableCell
				key={`cell-${aircraftGroupId}-${group.usergroup_id}`}
				aircraftGroupId={aircraftGroupId}
				userGroupId={group.usergroup_id}
				initCheck={!!initCheckList[group.usergroup_id]}
				editMode={editMode}
				editable={true}
				ref={ref}
			/>
		))
	);
}));

const UserGroupLinkageTable = memo(forwardRef(({filterWord, setWaiting, editMode, changeCoPilotSetting, changeAircraftSettingMenu, tableRef}, ref) => {
	const {
		aircraftGroupList,
		userGroupList,
		aircraftGroupOfUserGroupsList,
		updateAircraftGroupLinkageUserGroup,
		refresher,
	} = useAircraftGroupLinkageUserGroup();
	const checkRef = useRef({});
	const [reset, setReset] = useRecoilState(resetUserGroupState);
	const setAircraftGroupListFilterWord = useSetRecoilState(aircraftGroupListFilterWordState);
	const setAircraftGroupExternalListFilterWord = useSetRecoilState(aircraftGroupExternalListFilterWordState);
	const [userGroupSelectedId, setUserGroupSelectedId] = useRecoilState(userGroupSelectedIdState);
	const { showError, showWarning } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const list = useCallback(() => {
		if (filterWord === "") {
			return aircraftGroupList;
		}
		return aircraftGroupList.filter(row => {
			return columns.some(col => {
				switch (col.id) {
					default:
						const cell = row[col.id] && ((col.format && col.format(row[col.id])) || row[col.id]);
						return cell !== undefined && cell !== null && String(cell).indexOf(filterWord) !== -1;
				}
			});
		});
	}, [aircraftGroupList, filterWord]);

	const [enableUserGroups, setEnableUserGroups] = useState(() => {
		let check = true;
		if (userGroupSelectedId) {
			check = false;
		}
		return Object.fromEntries(userGroupList.map(group => {
			return [group.usergroup_id, group.usergroup_id === userGroupSelectedId || check];
		}));
	});

	const filterdUserGroupList = useMemo(() => {
		return userGroupList.filter(group => enableUserGroups[group.usergroup_id]);
	}, [userGroupList, enableUserGroups]);

	const checkList = useMemo(() => {
		const list = Object.fromEntries(Object.entries(aircraftGroupOfUserGroupsList).map(([agid, amap]) =>
			[agid, Object.fromEntries(Object.entries(amap).map(([aid, adata]) =>
				[aid, !!adata.isvalid]))
			]
		));
		return list;
	}, [aircraftGroupOfUserGroupsList]);

	useEffect(() => {
		return () => {
			setUserGroupSelectedId(null);
			refresher();
		}
	}, [refresher, setUserGroupSelectedId]);

	useImperativeHandle(ref, () => ({
		update: (updateData) => {
			setWaiting(true);
			updateAircraftGroupLinkageUserGroup(updateData)
				.then(() => {
					// console.log("update comp");
				})
				.catch(e => {
					console.error("update error", e);					if (e?.response?.status === 409 || e?.response?.status === 428) {
						showWarning(t('validate_optimistic_lock'));
						refresher();
					} else {
						showError(`${t('Update failed')}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		check: (item, value) => {
			return aircraftGroupList.every(v => v[item] !== value);
		},
		save: handleSave,
		cancel: handleCancel,
	}));

	const handleSave = () => {
		const data = [];
		Object.entries(checkRef.current).forEach(([agid, list]) => {
			Object.entries(list).forEach(([ugid, valid]) => {
				const _data = aircraftGroupOfUserGroupsList[agid][ugid];
				if (_data?.isvalid !== valid) {
					if (valid) {
						data.push({
							aircraftgroup_id: agid,
							usergroup_id: ugid,
							contract_id: _data.contract_id,
							update_time: null,
						});
					} else {
						data.push({
							aircraftgroup_id: agid,
							usergroup_id: ugid,
							contract_id: _data.contract_id,
							update_by_user: _data.update_by_user,
							update_time: _data.update_time,
						});
					}
				}
			})
		});
		if (data.length > 0) {
			ref.current.update(data);
		}
	}

	const handleCancel = () => {
		setReset(!reset);
	}

	const handleClickAircraftGroup = useCallback(agid => {
		if (agid) {
			setAircraftGroupListFilterWord(agid);
		}
		changeAircraftSettingMenu(AircraftSettingMenu.AIRCRAFT_GROUP);
	}, [changeAircraftSettingMenu, setAircraftGroupListFilterWord]);

	const handleClickAircraftGroupExternal = useCallback(agid => {
		if (agid) {
			setAircraftGroupExternalListFilterWord(agid);
		}
		changeAircraftSettingMenu(AircraftSettingMenu.AIRCRAFT_GROUP);
	}, [changeAircraftSettingMenu, setAircraftGroupExternalListFilterWord]);

	return (
		<>
			<Box className={classes.filterIconBox}>
				<SelectCheckboxList
					title={t('Filter Column')}
					dataList={userGroupList}
					enableList={enableUserGroups}
					setEnableList={setEnableUserGroups}
					icon={<ViewColumnIcon />}
				/>
			</Box>
			<TableContainer className={classes.container} ref={tableRef}>
				<Table aria-label="sticky table" size="small" className={classes.stickyTable}>
					<TableHead>
						<TableRow className={classes.tableRow}>
							{columns.map((column) => (
								<TableCell
									key={column.id}
									align={column.align}
									style={{ minWidth: 0 }}
									className={clsx(classes.headerCell, classes.tableCell)}
									{...column.id === 'usergroup_name' ? {colSpan: userGroupList.length} : {rowSpan: 2}}
								>
									{
										column.id === 'aircraftgroup_name' ? 
											<OnClickAnchor onClick={() => handleClickAircraftGroup()}>{t(column.label)}</OnClickAnchor> :
										t(column.label)
									}
								</TableCell>
							))}
						</TableRow>
						<TableRow className={classes.tableRow}>
							{filterdUserGroupList.map(group => (
								<UserGroupListTableHeaderCell
									key={`head-${group.usergroup_id}`}
									userGroupId={group.usergroup_id}
									userGroupName={group.usergroup_name}
									editMode={editMode}
									editable={true}
								/>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{list().map((row) => {
							return (
								<TableRow hover role="checkbox" tabIndex={-1} key={row.aircraftgroup_id}>
									{columns.map((column) => {
										const value = row[column.id];
										if (column.id !== 'usergroup_name') {
											return (
												<TableCell
													key={column.id}
													align={column.align}
													className={clsx(
														classes.tableCell,
														column.id === 'aircraft_number' && classes.alignRight,
														row.aircraftgroup_type === 1 && classes.commission,
														row.aircraftgroup_type === 2 && classes.agreement,
													)}
												>
													{
														column.id === 'aircraftgroup_name' ?
															<OnClickAnchor onClick={() => {
																if (row.aircraftgroup_type === 0) {
																	return handleClickAircraftGroup(row.aircraftgroup_name);
																} else {
																	return handleClickAircraftGroupExternal(row.aircraftgroup_name);
																}
															}}>{value}</OnClickAnchor> :
														column.format && typeof value === 'number' ? column.format(value) : value
													}
												</TableCell>
											);
										} else {
											return (
												<UserGroupListTableRow
													key={column.id}
													userGroupList={filterdUserGroupList}
													aircraftGroupId={row.aircraftgroup_id}
													initCheckList={checkList[row.aircraftgroup_id]}
													editMode={editMode}
													ref={checkRef}
												/>
											);
										}
									})}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
}));

function AircraftGroupLinkageUserGroup({changeCoPilotSetting, changeAircraftSettingMenu}) {
	const tableRef = useRef(null);
	const childRef = useRef({});
	const inputFilterRef = useRef(null);
	const [filterWord, setFileterWord] = useState('');
	const [editMode, setEditMode] = useState(false);
	const [waiting, setWaiting] = useState(false);

	const classes = useStyles();
	const {t} = useTranslation();

	useEffect(() => {
		return () => {
			setFileterWord('');
		}
	}, [setFileterWord]);

	const handleSave = () => {
		if (editMode) {
			childRef.current.save();
		}
		setEditMode(!editMode);
	}

	const handleCancel = () => {
		childRef.current.cancel();
		setEditMode(false);
	}

	const handleSearch = () => {
		const word = inputFilterRef.current.value;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleDeleteFilter = () => {
		const word = "";
		inputFilterRef.current.value = word;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	return (
		<Box style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 50px 10px 10px' }}>
				<Paper component="form" className={classes.filterBox} onSubmit={(event) => { handleSearch(event); event.preventDefault(); }}>
					<div className={classes.searchIcon}>
						<SearchIcon />
					</div>
					<InputBase
						className={classes.input}
						placeholder={`${t('Aircraft Group')}`}
						inputProps={{ 'aria-label': 'filter aircraft group list' }}
						inputRef={inputFilterRef}
						defaultValue={filterWord}
						// onChange={handleChange}
					/>
					{filterWord.length > 0 && <IconButton type="button" className={classes.iconButton} aria-label="delete" onClick={handleDeleteFilter}>
						<CloseIcon />
					</IconButton>}
				</Paper>
				<Box style={{ display: 'flex', justifyContent: 'flex-end', flex: 1 }}>
					<RoleControl subject={SUBJ_AIRCRAFT_GROUP_LINKAGE_USER_GROUP} verb={VERB_UPDATE}>
						{editMode ? <Button variant="outlined" size="small" className={classes.btn} style={{ marginRight: 20 }} onClick={handleCancel}>{t('Cancel')}</Button> : <div/>}
						<Button variant="outlined" size="small" className={classes.btn} onClick={handleSave}>{!editMode ? t('EDIT') : t('save')}</Button>
					</RoleControl>
				</Box>
			</Box>
			<Box style={{ padding: '0 10px 10px', flexGrow: 1, overflow: 'hidden' }}>
				{waiting && <Loading backdrop={true} />}
				<Suspense fallback={<Loading />}>
					<UserGroupLinkageTable filterWord={filterWord} ref={childRef} setWaiting={setWaiting} editMode={editMode} changeCoPilotSetting={changeCoPilotSetting} changeAircraftSettingMenu={changeAircraftSettingMenu} tableRef={tableRef}/>
				</Suspense>
			</Box>
		</Box>
	)
}

export default AircraftGroupLinkageUserGroup;
